"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { FieldWrapper } from "./context";
import { HoursQuickSelect } from "./HoursQuickSelect";
import type { FieldRenderContext } from "./context";

export { ProbabilitySliderFieldRenderer } from "./ProbabilitySliderFieldRenderer";

/**
 * Render a text field.
 */
export const TextFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const isHoursTextField =
    fieldName.toLowerCase().includes("hours") ||
    fieldName.toLowerCase().includes("hour") ||
    field.label.toLowerCase().includes("hours") ||
    field.label.toLowerCase().includes("hour");
  const textCurrentValue = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
    >
      <Input
        id={fieldName}
        {...register(fieldName)}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />

      {/* Quick select options for hours field */}
      {isHoursTextField && (
        <HoursQuickSelect
          currentValue={textCurrentValue}
          onSelect={(value) => setValue(fieldName, value as string, { shouldValidate: true })}
          isNumberField={false}
        />
      )}
    </FieldWrapper>
  );
};

/**
 * Render a textarea field.
 */
export const TextareaFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
    >
      <Textarea
        id={fieldName}
        {...register(fieldName)}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
        rows={4}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />
    </FieldWrapper>
  );
};

/**
 * Render a number field.
 */
export const NumberFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const isHoursField =
    field.label.toLowerCase().includes("hours") || field.label.toLowerCase().includes("hour");
  const currentValue = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
    >
      <Input
        id={fieldName}
        type="number"
        {...register(fieldName, {
          setValueAs: (value) => {
            // Handle empty string, null, or undefined
            if (value === "" || value === null || value === undefined) {
              return field.required ? NaN : undefined;
            }

            // Convert to number
            const numValue = Number(value);

            // Check if conversion resulted in NaN
            if (isNaN(numValue)) {
              return field.required ? NaN : undefined;
            }

            return numValue;
          },
        })}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
        min={field.min}
        max={field.max}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />

      {/* Quick select options for hours field */}
      {isHoursField && (
        <HoursQuickSelect
          currentValue={currentValue}
          onSelect={(value) => setValue(fieldName, value as number, { shouldValidate: true })}
          isNumberField={true}
        />
      )}
    </FieldWrapper>
  );
};

/**
 * Render a boolean field (switch).
 */
export const BooleanFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const booleanValue = watchedValues[fieldName];

  return (
    <div key={fieldName} className={cn("space-y-2", hasError && "animate-shake")}>
      <div
        className={cn(
          "flex items-center space-x-2 p-3 rounded-lg border",
          hasError ? "bg-red-50 border-red-300 border-2 dark:bg-red-950/40 dark:border-red-800" : "bg-card-50"
        )}
      >
        <Switch
          checked={booleanValue || false}
          onCheckedChange={(checked: boolean) => setValue(fieldName, checked)}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${fieldName}-error` : undefined}
        />
        <label htmlFor={fieldName} className={hasError ? "text-red-700 font-semibold dark:text-red-300" : ""}>
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
      </div>
      {hasError && (
        <p
          id={`${fieldName}-error`}
          className="text-sm text-red-600 font-medium flex items-start gap-1"
        >
          <span>{errorMessage}</span>
        </p>
      )}
    </div>
  );
};

/**
 * Default field renderer for unknown types.
 */
export const DefaultFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
    >
      <Input
        id={fieldName}
        {...register(fieldName)}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />
    </FieldWrapper>
  );
};
